/**
 * Shapes mirrored from backend `admin-vendor` module. Kept manually for now —
 * future task could generate from the OpenAPI spec.
 */

export type KycStatus = 'PENDING' | 'DOCUMENTS_SUBMITTED' | 'VERIFIED' | 'REJECTED' | 'SUSPENDED';

export type VendorModuleType =
  | 'VET'
  | 'WALKER'
  | 'TRAINER'
  | 'GROOMER'
  | 'CAFE'
  | 'RESTAURANT'
  | 'FOOD'
  | 'PHARMACY'
  | 'LAB'
  | 'CLINIC'
  | 'CREMATION'
  | 'ADOPTION';

/** Human-readable labels for `VendorModuleType`. RESTAURANT is the dining vertical
 * (previously onboarded as FOOD); FOOD now means a packaged-food product vendor. */
export const VENDOR_MODULE_TYPE_LABELS: Record<VendorModuleType, string> = {
  VET: 'Veterinary Clinic',
  WALKER: 'Pet Walker',
  TRAINER: 'Pet Trainer',
  GROOMER: 'Pet Groomer',
  CAFE: 'Pet Café',
  RESTAURANT: 'Restaurant',
  FOOD: 'Food & products',
  PHARMACY: 'Pharmacy',
  LAB: 'Diagnostics Lab',
  CLINIC: 'Clinic',
  CREMATION: 'Cremation Service',
  ADOPTION: 'Adoption Center',
};

export type VendorListItem = {
  id: string;
  business_name: string | null;
  owner_name: string | null;
  city: string | null;
  state: string | null;
  module_types: VendorModuleType[];
  kyc_status: KycStatus;
  is_active: boolean;
  /** Decimal(2,1) as a string, so no precision is lost in JSON. */
  avg_rating: string;
  review_count: number;
  created_at: string;
  updated_at: string;
  service_count: number;
  kyc_document_count: number;
  /**
   * Whether payout details exist. A VERIFIED vendor without them takes
   * bookings and then cannot be settled, which is why it earns a column.
   */
  has_payout: boolean;
};

export type VendorListResponse = {
  items: VendorListItem[];
  total: number;
  page: number;
  page_size: number;
};

export type VendorKycDocument = {
  id: string;
  type: string;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  uploaded_at: string;
  verified_at: string | null;
  /**
   * Display name of the admin who ticked it, or null.
   *
   * These two columns existed from the start with nothing writing them, so every
   * document read "Not marked verified" forever - including on approved vendors.
   * Approval is still vendor-level; this records that a person read the paper.
   */
  verified_by_name: string | null;
};

export type VendorPhoto = {
  id: string;
  type: string;
  s3_key: string;
  s3_key_medium: string | null;
  s3_key_thumb: string | null;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  display_order: number;
  uploaded_at: string;
};

export type VendorAudit = {
  id: string;
  from_status: KycStatus | null;
  to_status: KycStatus;
  reason: string | null;
  changed_by_id: string;
  /** Display name of the admin who made the decision; null if unresolvable. */
  changed_by_name: string | null;
  changed_at: string;
};

/**
 * One prior job a walker or trainer declared during onboarding.
 *
 * Checkable claims — a named employer and a date range — as opposed to the single
 * `years_of_experience` number, which nobody can verify. The KYC reviewer reads both.
 */
export type VendorExperienceEntry = {
  id: string;
  module_type: string;
  role: string;
  organisation: string | null;
  /** YYYY-MM-DD; only month precision is meaningful. */
  from_month: string;
  to_month: string | null;
  is_current: boolean;
  description: string | null;
  /** Whole months between the dates, counted to today when still current. */
  months: number;
};

/**
 * Per-module service details (mirrors the backend admin serializer). Each key
 * is present only when the vendor has that module sub-profile. Codes/enums are
 * raw backend values; the UI humanizes them.
 */
export type VendorModuleDetails = {
  CAFE?: {
    opening_time: string;
    closing_time: string;
    years_active: number | null;
  };
  WALKER?: {
    full_name: string;
    date_of_birth: string | null;
    gender: string | null;
    service_radius: string;
    years_of_experience: number;
    specialization_codes: string[];
    language_codes: string[];
    experience: VendorExperienceEntry[];
  };
  TRAINER?: {
    full_name: string;
    date_of_birth: string | null;
    gender: string | null;
    /** Every mode the trainer offers. Multi-select since 2026-08-27. */
    training_mode: string[];
    service_radius: string;
    years_of_experience: number;
    trainer_for_species: string[];
    experience: VendorExperienceEntry[];
  };
  GROOMER?: {
    full_name: string;
    date_of_birth: string | null;
    gender: string | null;
    salon_name: string | null;
    service_radius: string;
    years_of_experience: number;
    groomer_for_species: string[];
  };
  RESTAURANT?: {
    /** Every kind the venue is — cafe, bakery, restaurant. Multi-select since 2026-08-27. */
    restaurant_type: string[];
    delivery_radius: string | null;
    fssai_license_number: string | null;
    pet_friendly_species: string[];
    services_offered: string[];
    opening_time: string;
    closing_time: string;
  };
  /** Packaged-food product vendor. Shape kept loose — surfaced generically until pinned down. */
  FOOD?: Record<string, unknown>;
  PHARMACY?: {
    drug_license_number: string;
  };
  ADOPTION?: {
    organization_types: string[];
    contact_person_name: string;
    designation: string | null;
    animals_available: number | null;
    max_capacity: number | null;
    volunteer_count: number | null;
    year_established: string | null;
    staff_count: number;
  };
  CREMATION?: {
    service_radius: string | null;
    home_pickup_available: boolean;
    is_24x7_emergency: boolean;
    cremation_services: string[];
  };
  VET?: {
    vet_full_name: string;
    medical_degree: string | null;
    years_of_experience: number;
    registration_number: string;
    session_fee_inr: string;
    pet_types_treated: string[];
    specialization_codes: string[];
  };
};

export type SchedulingConfig = { min_notice_hours: number; horizon_days: number };

/** PATCH body — at least one field; omitted fields keep their current value. */
export type UpdateSchedulingRequest = Partial<SchedulingConfig>;

/**
 * Where a vendor is paid, masked.
 *
 * The account number and UPI id are encrypted server-side and are never returned to anyone,
 * admins included — only the last four digits and the pre-masked handle. An admin console is not
 * a reason to decrypt someone's bank account.
 */
/**
 * The UNMASKED destination, returned only by the audited reveal endpoint.
 *
 * Never store this in component state or a query cache. It exists for the few
 * milliseconds between the response landing and `navigator.clipboard.writeText`.
 */
export type PayoutSecret = {
  method: 'BANK_ACCOUNT' | 'UPI';
  account_holder_name: string | null;
  account_number: string | null;
  ifsc_code: string | null;
  upi_id: string | null;
};

export type VendorPayout = {
  method: 'BANK_ACCOUNT' | 'UPI';
  account_holder_name: string | null;
  account_number_last4: string | null;
  ifsc_code: string | null;
  upi_id_display: string | null;
  verified_at: string | null;
  last_changed_at: string | null;
  change_count: number;
  can_edit: boolean;
  next_editable_at: string | null;
  cooldown_days: number;
};

export type VendorDetail = VendorListItem & {
  description: string | null;
  email_for_business: string | null;
  /** OTP login phone of the owning account (read-only). */
  account_phone: string | null;
  /** Owning User account id, for cross-linking to the customer record. */
  owner_user_id: string | null;
  /** Admin-scoped path to the owner's profile photo, or null when unset. */
  owner_avatar_url: string | null;
  /** When an admin last asked this vendor to add payout details, and who asked. */
  payout_requested_at: string | null;
  payout_requested_by_name: string | null;
  /** Which animals this vendor will accept. */
  services_species: string[];
  /** Third scheduling rule: how close to a slot a customer may reschedule. */
  reschedule_min_notice_hours: number;
  instagram_url: string | null;
  facebook_url: string | null;
  /**
   * Decimal(4,1) as a string. INERT — defaults to 5 and no query reads it; the radius
   * discovery matches on lives on the module profile as `service_radius`. Kept only so
   * older builds do not break. Do not surface it as a vendor's coverage.
   */
  delivery_radius_km: string;
  /** Optional public-facing business phone. */
  business_phone: string | null;
  website_url: string | null;
  gst_number: string | null;
  business_registration_number: string | null;
  is_24x7: boolean;
  scheduling: SchedulingConfig;
  /**
   * Null means no payout details at all — only reachable for accounts predating the rule
   * that made the step mandatory. Those vendors cannot be settled, so it is worth showing.
   */
  payout: VendorPayout | null;
  address: string | null;
  landmark: string | null;
  state: string | null;
  pincode: string | null;
  latitude: string | null;
  longitude: string | null;
  kyc_documents: VendorKycDocument[];
  photos: VendorPhoto[];
  audits: VendorAudit[];
  module_details: VendorModuleDetails;
  services: VendorServiceItem[];
  dining: DiningCaps | null;
  dining_offerings: DiningOfferingItem[];
  grooming_packages: GroomingPackageItem[];
};

export type CatalogImageItem = {
  id: string;
  thumb_path: string;
  full_path: string;
  /**
   * False while a photo added to an already-approved listing waits for review —
   * shoppers are not being shown it. Absent on user-scoped payloads, which never
   * carry unapproved rows.
   */
  is_approved?: boolean;
  display_order?: number;
};

export type VendorServiceItem = {
  id: string;
  module_type: VendorModuleType;
  name: string;
  description: string | null;
  price_inr: string;
  duration_minutes: number | null;
  /** CREMATION services only — every offering this service covers. Multi since 2026-08-27. */
  cremation_service_type: string[];
  /**
   * Which pets this service is for.
   *
   * Always explicit — the server resolves an empty list to the vendor's own
   * species when saving, so this never needs an "empty means all" reading.
   */
  species: string[];
  /** LAB tests only — the sample the test needs. */
  lab_sample_type: string | null;
  /** LAB tests only — whether the pet must fast, and the usual report time. */
  lab_fasting_required: boolean | null;
  lab_turnaround_hours: number | null;
  /** TRAINER services only — where the session happens. */
  training_mode: string | null;
  is_active: boolean;
  display_order: number;
  /** How many bookings this service has taken. */
  booking_count: number;
  images: CatalogImageItem[];
};

/** Hiding or restoring one service. The reason is repeated to the vendor. */
export type ServiceVisibilityInput = { is_active: boolean; reason: string };

export type ServiceVisibilityResult = {
  id: string;
  name: string;
  is_active: boolean;
  /** False when the service was already in the requested state. */
  changed: boolean;
};

export type DiningCaps = {
  kind: string;
  dine_in: boolean;
  delivery: boolean;
  pickup: boolean;
  walk_in: boolean;
  opening_time: string;
  closing_time: string;
};

export type DiningBookingMode = 'TABLE_RESERVATION' | 'ENTRY_PASS' | 'EVENT';

export type DiningOfferingItem = {
  id: string;
  service_id: string;
  mode: DiningBookingMode;
  name: string;
  description: string | null;
  price_inr: string;
  price_unit: string;
  is_active: boolean;
  min_party: number;
  max_party: number;
  turn_minutes: number | null;
  capacity: number;
  event_start: string | null;
  event_end: string | null;
  images: CatalogImageItem[];
};

export type GroomingTier = 'BASIC' | 'PREMIUM' | 'DELUXE';

/** Grooming package tiers — a `GroomingPackage` 1:1 on a GROOMER `VendorService`. */
export type GroomingPackageItem = {
  id: string;
  service_id: string;
  vendor_profile_id: string;
  tier: GroomingTier;
  name: string;
  description: string | null;
  price_inr: string;
  duration_minutes: number | null;
  mrp_inr: string | null;
  discount_pct: number | null;
  included_items: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type StatusChangeResponse = {
  id: string;
  kyc_status: KycStatus;
  audit_id: string;
};

export type PresignedUrlResponse = {
  url: string;
  expires_in_seconds: number;
};

export type ListFilters = {
  page?: number;
  page_size?: number;
  status?: KycStatus;
  /** Several statuses at once, comma-separated. Beats `status` when both are set. */
  statuses?: string;
  module_type?: VendorModuleType;
  city?: string;
  state?: string;
  /** '' = either, 'true' = active only, 'false' = deactivated only. */
  is_active?: string;
  has_payout?: string;
  min_rating?: number;
  /** Inclusive created-date range, YYYY-MM-DD. */
  from?: string;
  to?: string;
  /** Business or owner name search. */
  q?: string;
};

/** Columns the API can sort by — mirrors VENDOR_SORT_FIELDS on the backend. */
export const VENDOR_SORT_FIELDS = [
  'created_at',
  'updated_at',
  'business_name',
  'city',
  'kyc_status',
  'avg_rating',
  'review_count',
] as const;

export const KYC_STATUS_OPTIONS: KycStatus[] = [
  'PENDING',
  'DOCUMENTS_SUBMITTED',
  'VERIFIED',
  'REJECTED',
  'SUSPENDED',
];

/** Every `VendorModuleType`, so the admin filter can reach all of them. */
export const MODULE_TYPE_OPTIONS: VendorModuleType[] = [
  'VET',
  'WALKER',
  'TRAINER',
  'GROOMER',
  'CAFE',
  'RESTAURANT',
  'FOOD',
  'PHARMACY',
  'LAB',
  'CLINIC',
  'CREMATION',
  'ADOPTION',
];
